import axios from 'axios'
import type {TargetedMessagesParams} from '~/types/targeted-messages'
import {TARGETED_MESSAGES_PARAM_MAPPING} from '~/assets/js/constants'

// Helper function to add filters to an OR condition in Strapi v3.6.x format
const addOrCondition = (
  queryParams: Record<string, any>,
  index: number,
  salesArea: string | string[] | undefined,
  companyFilter: Record<string, any>,
  userType?: string | string[]
) => {
  const mappedSalesAreaKey = TARGETED_MESSAGES_PARAM_MAPPING['salesAreaContains'] || 'salesArea'
  const mappedUserTypeKey = TARGETED_MESSAGES_PARAM_MAPPING['userTypeContains'] || 'userType'

  queryParams[`_where[_or][${index}][${mappedSalesAreaKey}]`] = salesArea
  Object.entries(companyFilter).forEach(([key, value]) => {
    queryParams[`_where[_or][${index}][${key}]`] = value
  })
  if (userType) {
    queryParams[`_where[_or][${index}][${mappedUserTypeKey}]`] = userType
  }
}

export const useTargetedMessagesService = () => {
  let runtimeConfig: any
  try {
    runtimeConfig = useRuntimeConfig()
  } catch {
    // useRuntimeConfig not available, must be used out of context
    runtimeConfig = {
      public: process.env
    }
  }

  const targeted_messages_api_config = axios.create({
    baseURL: runtimeConfig.public.TARGETED_MESSAGES_API_URL || runtimeConfig.public.CMS_URL || 'https://cms-stg.dev.fst.com'
  })

  const getTargetedMessages = async function (params?: TargetedMessagesParams) {
    if (!params || (!params?.salesArea && !params?.companyNo)) {
      return []
    }

    const now = new Date().toISOString()
    const mappedCompanyNoKey = TARGETED_MESSAGES_PARAM_MAPPING['companyNoContains'] || 'companyNo'
    const companyNoKey = TARGETED_MESSAGES_PARAM_MAPPING['companyNo'] || 'CompanyNo'
    const companyNoNullKey = TARGETED_MESSAGES_PARAM_MAPPING['companyNoNull'] || 'CompanyNo_null'
    const startLteKey = TARGETED_MESSAGES_PARAM_MAPPING['startLte'] || 'Start_lte'
    const endGtKey = TARGETED_MESSAGES_PARAM_MAPPING['endGt'] || 'End_gt'
    const localeKey = TARGETED_MESSAGES_PARAM_MAPPING['_locale'] || '_locale'

    const queryParams: Record<string, any> = {
      [startLteKey]: now,
      [endGtKey]: now
    }

    if (params._locale) {
      queryParams[localeKey] = params._locale
    }

    // Create OR conditions for: specific company, empty company, or null company
    addOrCondition(queryParams, 0, params.salesArea, {[mappedCompanyNoKey]: params.companyNo}, params.userType)
    addOrCondition(queryParams, 1, params.salesArea, {[companyNoKey]: ''}, params.userType)
    addOrCondition(queryParams, 2, params.salesArea, {[companyNoNullKey]: true}, params.userType)

    const response = await targeted_messages_api_config.get('targeted-messages', {
      params: queryParams
    }).catch(() => ({data: []}))

    return response?.data ?? []
  }

  return {
    getTargetedMessages
  }
}
